import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  Button,
  ChevronLeftIcon,
  H2,
  LoadingDots,
  Span,
  Text,
} from '@/components/atoms'
import { ClaimCard, FilterChips, StickyFooter } from '@/components/molecules'
import { ROUTES } from '@/shared/constants/routes'
import { useClaimsPage } from './page.claims.hook'

export function ClaimsPage() {
  const { t } = useTranslation()
  const {
    claims,
    filters,
    activeKind,
    handleSelectKind,
    isLoading,
    isFetchingPage,
    isError,
    handleRetry,
    page,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    handlePreviousPage,
    handleNextPage,
    handleOpenClaim,
    handleFileClaim,
  } = useClaimsPage()

  return (
    <div className="flex min-h-dvh flex-col bg-white">
      <div className="mx-auto flex w-full max-w-266.5 flex-1 flex-col gap-8 px-6 pt-6 pb-8 md:px-0 md:pb-32">
        <Link
          to={ROUTES.HOME}
          className="text-indigo-base flex w-fit items-center gap-1 text-sm"
          aria-label={t('claims.pet.back')}
          data-testid="claims-back"
        >
          <ChevronLeftIcon />
        </Link>

        <div className="flex flex-col gap-4 md:gap-6">
          <H2 className="text-[2rem]! leading-10! md:text-[3.125rem]! md:leading-15!">
            <Span className="text-neutral-900" textSize="inherit">
              {t('claims.list.titleStart')}
            </Span>
            <br />
            <Span className="text-indigo-base" textSize="inherit">
              {t('claims.list.titleHighlight')}
            </Span>
          </H2>
          <Text className="text-neutral-900">
            {t('claims.list.description')}
          </Text>
        </div>

        <FilterChips
          filters={filters}
          active={activeKind}
          onSelect={handleSelectKind}
        />

        {isLoading ? (
          <div className="flex justify-center py-16">
            <LoadingDots className="loading-xl" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <Text className="text-neutral-600">{t('claims.list.error')}</Text>
            <Button variant="secondary" onClick={handleRetry}>
              {t('claims.list.retry')}
            </Button>
          </div>
        ) : !claims.length ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-neutral-300 bg-(--file-upload-bg) px-6 py-16 text-center">
            <Text className="font-bold text-neutral-900">
              {t(`claims.list.empty.${activeKind}.title`)}
            </Text>
            <Text className="text-neutral-600">
              {t(`claims.list.empty.${activeKind}.description`)}
            </Text>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div
              className={`grid grid-cols-1 gap-4 transition-opacity md:grid-cols-2 ${isFetchingPage ? 'opacity-60' : ''}`}
            >
              {claims.map((claim) => (
                <ClaimCard
                  key={claim.id}
                  petName={claim.petName}
                  petPictureUrl={claim.petPictureUrl}
                  claimNumber={claim.claimNumber}
                  statusLabel={claim.statusLabel}
                  statusTone={claim.statusTone}
                  amount={claim.amount}
                  date={claim.date}
                  message={claim.message}
                  onClick={() => handleOpenClaim(claim.id)}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="secondary"
                  onClick={handlePreviousPage}
                  disabled={!hasPreviousPage || isFetchingPage}
                  data-testid="claims-previous-page"
                >
                  {t('claims.list.previous')}
                </Button>
                <Span className="text-neutral-600">
                  {t('claims.list.pageOf', { page, total: totalPages })}
                </Span>
                <Button
                  variant="secondary"
                  onClick={handleNextPage}
                  disabled={!hasNextPage || isFetchingPage}
                  data-testid="claims-next-page"
                >
                  {t('claims.list.next')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <StickyFooter>
        <Button
          size="lg"
          sizeMobile="md"
          onClick={handleFileClaim}
          className="h-12 w-full md:w-auto"
          data-testid="claims-file-claim"
        >
          {t('claims.list.fileClaim')}
        </Button>
      </StickyFooter>
    </div>
  )
}
